import { useCallback, useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  getIdleThresholdMinutes,
  IDLE_THRESHOLD_OPTIONS,
  setIdleThresholdMinutes,
  type IdleThreshold,
} from '../../../lib/idle-timer';
import { ChangePassphraseDialog } from '../../../features/auth/change-passphrase-dialog';
import { RotateRecoverySeedDialog } from '../../../features/auth/rotate-recovery-seed-dialog';
import { PasskeyList } from '../../../features/auth/passkey-list';
import { AddPasskeyButton } from '../../../features/auth/add-passkey-button';
import { useVaultStore } from '../../../features/auth/vaultStore';

function thresholdLabel(value: IdleThreshold): string {
  if (value === 'never') return 'Vault never auto-locks on this device.';
  return `Locks after ${value} minutes of inactivity.`;
}

function SecurityPage() {
  const status = useVaultStore((s) => s.status);
  const [threshold, setThreshold] = useState<IdleThreshold>(() => getIdleThresholdMinutes());
  const [changePassphraseOpen, setChangePassphraseOpen] = useState(false);
  const [rotateSeedOpen, setRotateSeedOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);

  // Re-sync the local state if the timer module loaded from localStorage
  // after this component mounted (idle-timer reads on startIdleTimer()
  // which runs in the authed shell mount).
  useEffect(() => {
    setThreshold(getIdleThresholdMinutes());
  }, []);

  const handleThresholdChange = useCallback((value: IdleThreshold) => {
    setThreshold(value);
    setIdleThresholdMinutes(value);
  }, []);

  if (status !== 'unlocked') {
    return (
      <main className="p-6 max-w-2xl">
        <h1 className="text-2xl font-display">Security</h1>
        <p className="text-sm mt-2 text-fg/70">
          Unlock the vault to manage passphrase, recovery seed, and passkeys.
        </p>
      </main>
    );
  }

  return (
    <main className="p-6 max-w-2xl space-y-10">
      <h1 className="text-2xl font-display">Security</h1>

      {flash && (
        <p
          role="status"
          className="text-sm text-green-700 border-l-2 border-green-700 pl-2"
          data-testid="settings-flash"
        >
          {flash}
        </p>
      )}

      <section className="space-y-2">
        <h2 className="text-sm uppercase text-fg/60">Auto-lock</h2>
        <p className="text-xs text-fg/60">{thresholdLabel(threshold)}</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Idle timeout">
          {IDLE_THRESHOLD_OPTIONS.map((opt) => {
            const isActive = opt === threshold;
            return (
              <button
                key={String(opt)}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => handleThresholdChange(opt)}
                className={`border px-3 py-1 text-sm ${
                  isActive ? 'bg-accent text-bg' : 'bg-transparent'
                }`}
                data-testid={`idle-threshold-${opt}`}
              >
                {opt === 'never' ? 'Never' : `${opt} min`}
              </button>
            );
          })}
        </div>
        {threshold === 'never' && (
          <p className="text-xs text-amber-700 italic">
            Closing the tab is the only manual lock when auto-lock is disabled.
          </p>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm uppercase text-fg/60">Daily passphrase</h2>
        <p className="text-xs text-fg/60">
          The passphrase you type to unlock the vault every day. Changing it re-wraps your Vault Key
          under a freshly derived Wrap Key.
        </p>
        <button
          type="button"
          onClick={() => setChangePassphraseOpen(true)}
          className="border px-3 py-1 text-sm"
          data-testid="open-change-passphrase"
        >
          Change passphrase
        </button>
        <ChangePassphraseDialog
          open={changePassphraseOpen}
          onOpenChange={setChangePassphraseOpen}
          onRotated={() => setFlash('Passphrase changed.')}
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm uppercase text-fg/60">Recovery seed</h2>
        <p className="text-xs text-fg/60">
          The 24-word fallback for when you forget your passphrase. We can&apos;t show you the
          existing one — we never stored it. You can rotate to a new seed (which invalidates the old
          one).
        </p>
        <button
          type="button"
          onClick={() => setRotateSeedOpen(true)}
          className="border px-3 py-1 text-sm"
          data-testid="open-rotate-seed"
        >
          Rotate recovery seed
        </button>
        <RotateRecoverySeedDialog
          open={rotateSeedOpen}
          onOpenChange={setRotateSeedOpen}
          onRotated={() => setFlash('Recovery seed rotated. Save the new one.')}
        />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm uppercase text-fg/60">Enrolled passkeys</h2>
        <PasskeyList refreshKey={refreshKey} />
        <div className="border-t pt-3">
          <AddPasskeyButton onAdded={() => setRefreshKey((n) => n + 1)} />
        </div>
      </section>
    </main>
  );
}

export const Route = createFileRoute('/_authed/app/settings/security')({
  component: SecurityPage,
});
